import React from 'react';
import { usePageSEO } from '../hooks/usePageSEO';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import './About.css';

const About = () => {
  const [storyRef, storyVisible] = useScrollAnimation(0.15);
  const [teamRef, teamVisible] = useScrollAnimation(0.1);
  const [valuesRef, valuesVisible] = useScrollAnimation(0.1);

  usePageSEO(
    'About Us | Mobile App, Website & Software Development Team - GoToBusiness',
    'Meet the GoToBusiness team. We are a Noida-based mobile application development, website development and software development company. Our story, mission and experts.'
  );

  const teamMembers = [
      {
      name: "Rohit Gupta",
      role: "Mobile App Developer",
      expertise: "React Native, Flutter, iOS/Android",
      image: "/images/team/rohit_gupta.jpg",
      description: "Specialized in cross-platform mobile development with a focus on user experience."
    },
  
    {
      name: "Rohan Kumar",
      role: "Full Stack Developer",
      expertise: "React, Node.js, MongoDB",
      image: "/images/team/tyagi.png",
      description: "5+ years of experience in web development with expertise in modern JavaScript frameworks."
    },
  
    {
      name: "Sonam Tyagi",
      role: "UI/UX Designer",
      expertise: "Figma, Adobe Creative Suite, User Research",
      image: "/images/team/sonam.png",
      description: "Creative designer focused on creating intuitive and engaging user interfaces."
    }
  ];

  return (
    <div className="about">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="container">
          <h1>About GoToBusiness</h1>
          <p>Transforming Ideas into Digital Reality</p>
        </div>
      </section>

      {/* Company Story */}
      <section ref={storyRef} className={`company-story section ${storyVisible ? 'animate' : ''}`}>
        <div className="container">
          <div className="story-content">
            <h2>Our Story</h2>
            <p>
              Founded in 2020, GoToBusiness emerged from a simple vision: to bridge the gap between 
              innovative ideas and their digital implementation. We started as a small team of passionate 
              developers who believed that every business, regardless of size, deserves access to 
              cutting-edge technology solutions.
            </p>
            <p>
              Today, we've grown into a comprehensive digital solutions provider, specializing in 
              website development, mobile applications, custom software, and Unity game development. 
              Our journey has been marked by successful partnerships with startups, SMEs, and 
              established enterprises across various industries.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="mission-vision section" style={{background: '#f8f9fa'}}>
        <div className="container">
          <div className="grid grid-2">
            <div className="mission card">
              <h3>Our Mission</h3>
              <p>
                To empower businesses with innovative digital solutions that drive growth, 
                enhance user experiences, and create lasting value. We strive to make 
                technology accessible and impactful for every client we serve.
              </p>
            </div>
            <div className="vision card">
              <h3>Our Vision</h3>
              <p>
                To be the leading digital transformation partner, recognized for our 
                technical excellence, creative solutions, and commitment to client success. 
                We envision a future where technology seamlessly integrates with business goals.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section ref={teamRef} className={`team section ${teamVisible ? 'animate' : ''}`}>
        <div className="container">
          <h2 className="text-center">Meet Our Team</h2>
          <p className="team-intro text-center">
            Our diverse team of experts brings together years of experience and passion for technology
          </p>
          
          <div className="team-grid">
            {teamMembers.map((member, index) => (
              <div key={index} className="team-member card">
                <div className="member-image">
                  <img 
                    src={member.image} 
                    alt={member.name} 
                    className="member-photo"
                  />
                </div>
                <div className="member-info">
                  <h4>{member.name}</h4>
                  <div className="member-role">{member.role}</div>
                  <div className="member-expertise">{member.expertise}</div>
                  <p>{member.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section ref={valuesRef} className={`values section ${valuesVisible ? 'animate' : ''}`} style={{background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'}}>
        <div className="container">
          <h2 className="text-center gradient-text">Our Core Values</h2>
          <p className="values-intro text-center">
            The principles that guide everything we do and define who we are as a company
          </p>
          <div className="values-grid">
            <div className="value-item excellence">
              <div className="value-icon-wrapper">
                <div className="value-icon">
                  🎯
                </div>
              </div>
              <div className="value-content">
                <h4>Excellence</h4>
                <p>We strive for perfection in every project, ensuring the highest quality deliverables that exceed expectations.</p>
              </div>
            </div>
            
            <div className="value-item collaboration">
              <div className="value-icon-wrapper">
                <div className="value-icon">
                  🤝
                </div>
              </div>
              <div className="value-content">
                <h4>Collaboration</h4>
                <p>We work closely with our clients as partners, fostering open communication and shared success.</p>
              </div>
            </div>
            
            <div className="value-item innovation">
              <div className="value-icon-wrapper">
                <div className="value-icon">
                  💡
                </div>
              </div>
              <div className="value-content">
                <h4>Innovation</h4>
                <p>We embrace new technologies and creative approaches to solve complex challenges and stay ahead.</p>
              </div>
            </div>
            
            <div className="value-item agility">
              <div className="value-icon-wrapper">
                <div className="value-icon">
                  ⚡
                </div>
              </div>
              <div className="value-content">
                <h4>Agility</h4>
                <p>We adapt quickly to changing requirements and market demands with flexible, responsive solutions.</p>
              </div>
            </div>
            
            <div className="value-item integrity">
              <div className="value-icon-wrapper">
                <div className="value-icon">
                  🔒
                </div>
              </div>
              <div className="value-content">
                <h4>Integrity</h4>
                <p>We maintain transparency and honesty in all our business relationships and decision-making processes.</p>
              </div>
            </div>
            
            <div className="value-item growth">
              <div className="value-icon-wrapper">
                <div className="value-icon">
                  🌱
                </div>
              </div>
              <div className="value-content">
                <h4>Growth</h4>
                <p>We are committed to continuous learning, improvement, and helping our clients achieve sustainable growth.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;